import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppHeader from './AppHeader';
import { isUnauthorizedError } from '../lib/httpUtils';
import { SYMBOL_DATA } from '../constants/symbols';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MAX_BACKTEST_SYMBOLS = 20;

const metricCards = [
  { key: 'trades', label: 'Trades', format: value => value },
  { key: 'win_rate_pct', label: 'Win rate', format: value => `${value}%` },
  { key: 'average_r', label: 'Average R', format: value => `${value}R` },
  { key: 'median_r', label: 'Median R', format: value => `${value}R` },
  { key: 'profit_factor', label: 'Profit factor', format: value => value ?? 'N/A' },
  { key: 'max_drawdown_r', label: 'Max drawdown', format: value => `${value}R` },
];

const BacktestMetrics = ({
  onLogout,
  onShowScanner,
  onShowEarnings,
  onShowBacktest,
  onShowAdmin,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [selectedSymbols, setSelectedSymbols] = useState(['SPY', 'QQQ', 'AAPL', 'MSFT', 'NVDA']);
  const [searchSymbol, setSearchSymbol] = useState('');
  const [period, setPeriod] = useState('60d');
  const [interval, setInterval] = useState('15m');
  const [lookback, setLookback] = useState('500');
  const [maxHoldingBars, setMaxHoldingBars] = useState('26');
  const [spreadBps, setSpreadBps] = useState('2');
  const [slippageBps, setSlippageBps] = useState('2');
  const [feePerShare, setFeePerShare] = useState('0');
  const [testStart, setTestStart] = useState('');
  const [targetClearance, setTargetClearance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const runBacktest = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);
    if (!selectedSymbols.length) {
      setError('Enter at least one ticker symbol.');
      return;
    }

    const token = window.localStorage.getItem('token');
    if (!token) {
      setError('You must be logged in to run a backtest.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/scanner/backtest`,
        {
          symbols: selectedSymbols,
          period,
          interval,
          lookback: Number(lookback),
          max_holding_bars: Number(maxHoldingBars),
          spread_bps: Number(spreadBps),
          slippage_bps: Number(slippageBps),
          fee_per_share: Number(feePerShare),
          test_start: testStart || null,
          require_target_clearance: targetClearance,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(response.data);
    } catch (requestError) {
      if (isUnauthorizedError(requestError)) {
        onLogout?.();
        return;
      }
      const detail = requestError.response?.data?.detail;
      setError(Array.isArray(detail)
        ? detail.map(item => item.msg || String(item)).join('; ')
        : detail || requestError.message || 'Unable to run the backtest.');
    } finally {
      setLoading(false);
    }
  };

  const summary = result?.summary;
  const searchTerms = searchSymbol
    .split(',')
    .map(term => term.trim().toLowerCase())
    .filter(Boolean);
  const filteredSymbols = searchTerms.length
    ? SYMBOL_DATA.filter(item => searchTerms.some(term =>
      item.symbol.toLowerCase().includes(term) || item.name.toLowerCase().includes(term)))
    : SYMBOL_DATA;
  const allFilteredSelected = filteredSymbols.length > 0
    && filteredSymbols.every(item => selectedSymbols.includes(item.symbol));
  const someFilteredSelected = filteredSymbols.some(item => selectedSymbols.includes(item.symbol))
    && !allFilteredSelected;

  const toggleSymbol = (symbol) => {
    setError('');
    if (!selectedSymbols.includes(symbol) && selectedSymbols.length >= MAX_BACKTEST_SYMBOLS) {
      setError(`A backtest can include at most ${MAX_BACKTEST_SYMBOLS} symbols.`);
      return;
    }
    setSelectedSymbols(current => current.includes(symbol)
      ? current.filter(item => item !== symbol)
      : [...current, symbol]);
  };

  const toggleFilteredSymbols = () => {
    setError('');
    const filtered = filteredSymbols.map(item => item.symbol);
    if (allFilteredSelected) {
      setSelectedSymbols(current => current.filter(symbol => !filtered.includes(symbol)));
      return;
    }
    const additions = filtered.filter(symbol => !selectedSymbols.includes(symbol));
    if (selectedSymbols.length + additions.length > MAX_BACKTEST_SYMBOLS) {
      setError(`Select no more than ${MAX_BACKTEST_SYMBOLS} symbols. Narrow your search or remove some symbols first.`);
      return;
    }
    setSelectedSymbols(current => [...new Set([...current, ...additions])]);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <AppHeader
        title={null}
        isAdmin={isAdmin}
        onShowScanner={onShowScanner}
        onShowEarnings={onShowEarnings}
        onShowBacktest={onShowBacktest}
        onShowAdmin={onShowAdmin}
        onLogout={onLogout}
      />
      <main className="mx-auto w-full max-w-7xl flex-1 p-4 sm:p-6">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t('backtest_metrics_label')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            Walk-forward results from historical candles. Entry is assumed at the confirmation close;
            spread and slippage are not included.
          </p>
        </header>

        <details className="mb-6 overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm">
          <summary className="cursor-pointer bg-blue-50 px-4 py-4 text-base font-semibold text-blue-950 hover:bg-blue-100 sm:px-6">
            How this backtest works — step by step
          </summary>
          <div className="space-y-5 px-4 py-5 sm:px-6">
            <ol className="grid gap-4 md:grid-cols-2">
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">1. Choose the test inputs</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Select up to 20 symbols and set the historical period, candle interval, level lookback,
                  and maximum holding time. Each symbol is downloaded from Yahoo Finance; the backtest
                  preserves the requested candle interval during data recovery.
                </p>
              </li>
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">2. Prepare historical candles</h2>
                <p className="mt-1 text-sm text-gray-600">
                  The test needs open, high, low, and close prices. Volume is not used. Intraday data is restricted
                  to regular U.S. market hours. Invalid or duplicate candles are removed before scanning.
                </p>
              </li>
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">3. Rebuild the signal from the past only</h2>
                <p className="mt-1 text-sm text-gray-600">
                  The scanner advances one candle at a time and re-detects support/resistance and
                  candidate signals using only the historical prefix ending at that candle. It starts
                  after an initial warm-up period, and only accepts a signal confirmed on the current
                  candle, so later candles are not used to decide whether that signal existed.
                </p>
              </li>
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">4. Apply the scanner’s setup rules</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Recent swing highs/lows are clustered into candidate resistance/support levels.
                  A level must pass the scanner’s quality-score threshold. A CALL needs a bullish
                  breakout, next-candle confirmation, a separate retest, and entry confirmation; a PUT uses
                  the inverse breakdown/retest conditions. The parameters are the scanner defaults,
                  not a parameter sweep.
                </p>
              </li>
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">5. Set entry, stop, and target</h2>
                <p className="mt-1 text-sm text-gray-600">
                  The confirmation close sets the reference stop and target. Entry is modeled at the next
                  available candle open, including spread and slippage. Gaps beyond the stop or target
                  skip entry. Realized reward-to-risk can differ from the reference 2R target.
                </p>
              </li>
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">6. Walk forward to an exit</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Exit simulation begins on the next candle and checks each candle’s high/low for the
                  stop or target, up to the maximum holding bars. If both levels are touched in one
                  candle, the stop is counted first. A gap through the stop uses the worse opening
                  price. If neither level is reached, exit occurs when the holding period completes.
                  Trades with insufficient future data remain unresolved. Opening gaps take precedence over intrabar ambiguity.
                </p>
              </li>
              <li className="rounded-lg border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">7. Review the metrics</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Results show individual simulated trades, totals by symbol, and overall win rate,
                  average/median R, profit factor, maximum drawdown in cumulative R, exit counts, and
                  average bars held. R is profit or loss divided by the entry-to-stop risk.
                </p>
              </li>
            </ol>
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <h2 className="font-semibold text-amber-950">Important limitations</h2>
              <p className="mt-1 text-sm text-amber-900">
                This is a trade-by-trade research estimate, not a portfolio simulator. It does not
                model available capital or overlapping-position limits. Costs are configurable assumptions.
                Historical data can be incomplete or unavailable, and results can differ from live
                execution. Compare different market periods and keep a separate out-of-sample period
                before using results to tune or rely on the scanner.
              </p>
            </div>
          </div>
        </details>

        <form onSubmit={runBacktest} className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="sm:col-span-2 lg:col-span-5">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-700">Search and select symbols</span>
                <span className="text-xs text-gray-500">{selectedSymbols.length}/{MAX_BACKTEST_SYMBOLS} selected</span>
              </div>
              <input
                type="text"
                value={searchSymbol}
                onChange={event => setSearchSymbol(event.target.value)}
                className="mb-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder={t('search_by_symbol')}
                aria-label="Search symbols by ticker or company name"
              />
              <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-300">
                {filteredSymbols.length > 0 ? (
                  <>
                    <label className="sticky top-0 z-10 flex min-h-10 w-full cursor-pointer items-center gap-3 border-b bg-gray-50 px-3 text-xs">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={element => {
                          if (element) element.indeterminate = someFilteredSelected;
                        }}
                        onChange={toggleFilteredSymbols}
                        className="h-4 w-4 shrink-0"
                      />
                      <span className="font-medium text-gray-700">
                        {allFilteredSelected ? t('deselect_all') : t('select_all')}
                      </span>
                      <span className="ml-auto text-gray-500">
                        {t('symbols_found_count', { count: filteredSymbols.length })}
                      </span>
                    </label>
                    {filteredSymbols.map(item => {
                      const isSelected = selectedSymbols.includes(item.symbol);
                      return (
                        <label
                          key={item.symbol}
                          className={`flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 hover:bg-blue-50 ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSymbol(item.symbol)}
                            className="h-4 w-4 shrink-0"
                          />
                          <span className="font-medium text-gray-800">{item.symbol}</span>
                          <span className="ml-auto text-right text-xs text-gray-500">{item.name}</span>
                        </label>
                      );
                    })}
                  </>
                ) : (
                  <p className="px-3 py-4 text-center text-sm text-gray-500">No matching symbols found.</p>
                )}
              </div>
              {selectedSymbols.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Selected symbols">
                  {selectedSymbols.map(symbol => (
                    <button
                      key={symbol}
                      type="button"
                      onClick={() => toggleSymbol(symbol)}
                      className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100"
                      aria-label={`Remove ${symbol}`}
                    >
                      {symbol} <span aria-hidden="true">×</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Period</span>
              <select value={period} onChange={event => setPeriod(event.target.value)} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="5d">5 days</option>
                <option value="10d">10 days</option>
                <option value="30d">30 days</option>
                <option value="60d">60 days</option>
                <option value="3mo">3 months</option>
                <option value="6mo">6 months</option>
                <option value="1y">1 year</option>
              </select>
            </label>
            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Interval</span>
              <select value={interval} onChange={event => setInterval(event.target.value)} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="5m">5 minutes</option>
                <option value="15m">15 minutes</option>
                <option value="30m">30 minutes</option>
                <option value="1h">1 hour</option>
                <option value="1d">1 day</option>
              </select>
            </label>
            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Level lookback (bars)</span>
              <input
                type="number"
                min="40"
                max="5000"
                value={lookback}
                onChange={event => setLookback(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label>
              <span className="mb-1 block text-sm font-medium text-gray-700">Max holding (bars)</span>
              <input
                type="number"
                min="1"
                max="500"
                value={maxHoldingBars}
                onChange={event => setMaxHoldingBars(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Running backtest…' : 'Run backtest'}
              </button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Spread (basis points)', spreadBps, setSpreadBps],
              ['Slippage per side (basis points)', slippageBps, setSlippageBps],
              ['Fee per share per side ($)', feePerShare, setFeePerShare],
            ].map(([label, value, setter]) => (
              <label key={label} className="text-sm">{label}
                <input type="number" required min="0" step="any" value={value}
                  onChange={event => setter(event.target.value)} className="mt-1 w-full rounded border p-2" />
              </label>
            ))}
            <label className="text-sm">Untouched test period starts (New York)
              <input type="date" value={testStart} onChange={event => setTestStart(event.target.value)}
                className="mt-1 w-full rounded border p-2" />
            </label>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={targetClearance} onChange={event => setTargetClearance(event.target.checked)} />
            Test target-clearance filter (experimental)
          </label>
          <p className="mt-2 text-sm text-gray-600">Entries use the next bar open with modeled costs. Default costs are assumptions, not broker quotes. Keep test dates untouched while choosing settings.</p>
          {error && <p role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {result?.errors && Object.keys(result.errors).length > 0 && (
            <div role="status" className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              Some symbols could not be loaded:
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(result.errors).map(([symbol, message]) => <li key={symbol}>{symbol}: {message}</li>)}
              </ul>
            </div>
          )}
        </form>

        {result && (
          <section aria-live="polite">
            <p className="mb-3 text-sm text-gray-600">Completed trades only; {summary?.unresolved ?? 0} unfinished signals and {summary?.entry_skipped ?? 0} skipped entries are excluded from returns. These are underlying-stock paper results, not option returns.</p>
            {result.validation?.holdout ? (
              <div className="mb-4 rounded border bg-blue-50 p-4 text-sm">
                <strong>Untouched test period</strong>: {result.validation.holdout.trades} completed trades;
                {' '}{result.validation.holdout.win_rate_pct}% profitable;
                {' '}{result.validation.holdout.average_r}R average after costs.
                <p>{result.validation.purged_boundary_trades} trades crossing the test boundary excluded from development results.</p>
              </div>
            ) : <p className="mb-4 text-sm text-amber-800">No untouched test period selected. These results are exploratory.</p>}
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Overall results</h2>
            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {metricCards.map(metric => (
                <div key={metric.key} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{metric.label}</p>
                  <p className="mt-2 text-xl font-semibold text-gray-900">
                    {metric.format(summary?.[metric.key])}
                  </p>
                </div>
              ))}
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <h3 className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-900">Results by symbol</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Symbol</th>
                        <th className="px-4 py-3">Trades</th>
                        <th className="px-4 py-3">Win rate</th>
                        <th className="px-4 py-3">Average R</th>
                        <th className="px-4 py-3">Profit factor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(result.by_symbol || {}).map(([symbol, metrics]) => (
                        <tr key={symbol}>
                          <td className="px-4 py-3 font-medium">{symbol}</td>
                          <td className="px-4 py-3">{metrics.trades}</td>
                          <td className="px-4 py-3">{metrics.win_rate_pct}%</td>
                          <td className="px-4 py-3">{metrics.average_r}R</td>
                          <td className="px-4 py-3">{metrics.profit_factor ?? 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="font-semibold text-gray-900">Exit breakdown</h3>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-gray-500">Targets</dt><dd className="font-semibold">{summary?.target_exits ?? 0}</dd></div>
                  <div><dt className="text-gray-500">Stops</dt><dd className="font-semibold">{summary?.stop_exits ?? 0}</dd></div>
                  <div><dt className="text-gray-500">Time limits</dt><dd className="font-semibold">{summary?.time_limit_exits ?? 0}</dd></div>
                  <div><dt className="text-gray-500">Average bars held</dt><dd className="font-semibold">{summary?.average_bars_held ?? 0}</dd></div>
                </dl>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <h3 className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-900">
                Simulated trades ({result.trades?.length ?? 0})
              </h3>
              <div className="max-h-[32rem] overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      {['Symbol', 'Side', 'Entry time', 'Entry', 'Stop', 'Target', 'Exit', 'Exit reason', 'R'].map(label => (
                        <th key={label} className="whitespace-nowrap px-4 py-3">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(result.trades || []).map((trade, index) => (
                      <tr key={`${trade.symbol}-${trade.entry_time}-${index}`}>
                        <td className="px-4 py-3 font-medium">{trade.symbol}</td>
                        <td className="px-4 py-3">{trade.signal_type}</td>
                        <td className="whitespace-nowrap px-4 py-3">{trade.entry_time}</td>
                        <td className="px-4 py-3">{trade.entry}</td>
                        <td className="px-4 py-3">{trade.stop}</td>
                        <td className="px-4 py-3">{trade.target}</td>
                        <td className="whitespace-nowrap px-4 py-3">{trade.exit_time}</td>
                        <td className="whitespace-nowrap px-4 py-3">{trade.exit_reason}</td>
                        <td className={`px-4 py-3 font-semibold ${trade.r_multiple > 0 ? 'text-green-700' : trade.r_multiple < 0 ? 'text-red-700' : ''}`}>
                          {trade.r_multiple == null ? 'Unresolved / skipped' : `${trade.r_multiple}R`}
                        </td>
                      </tr>
                    ))}
                    {!result.trades?.length && (
                      <tr><td colSpan="9" className="px-4 py-8 text-center text-gray-500">No qualifying trades found for this selection.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default BacktestMetrics;
